import type { DispatchTarget } from "@/modules/pos/components/dispatch/useFloorDispatch";
import type { ReactNode } from "react";
import { useTranslate } from "@/lib/i18n";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DeliveryTabGridEntry,
  DispatchBoardResponse,
  DriverSettlementsOpenResponse,
} from "@/lib/api/dispatchTypes";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import { useApiQuery } from "@/shared/queries";
import { isDispatchableBill, type BillDispatch } from "@/modules/pos/lib/dispatchBoard";
import {
  isUnsentEmpty,
  kitchenChip,
  pickupRows,
  unsentOwnRows,
  withDriverRows,
  type WaitingRow,
} from "@/modules/pos/lib/deliveryWaiting";
import { invoicesOnCards } from "@/modules/pos/lib/driverSettlementCards";
import { settlementsOpenQueryKey } from "@/modules/pos/lib/useDeliveredToasts";
import { OnlineOrdersStrip } from "../OnlineOrdersStrip";
import { DriverSettlementsCard } from "./DriverSettlementsCard";
import { PickupOrdersTable } from "./PickupOrdersTable";
import { WaitingForDriverCard } from "./WaitingForDriverCard";
import { WithDriverTable } from "./WithDriverTable";

/** T20's query key — under `dispatch`, refreshed with the board. */
export const settlementsOpenKey = settlementsOpenQueryKey;

/**
 * «التوصيل والتيك أواي» — the till's Delivery & takeaway tab (B6 · P24, plan
 * D-35 / D-36; P24b; laid out as the 2026-10-26 redesign). Top to bottom:
 *
 *  1. the customers' own requests (`OnlineOrdersStrip`) with the pause switch;
 *  2. the delivery flow as three lanes, the three things that happen to a bag
 *     (only for a session that may read the board):
 *     - **Waiting for a driver** — own bags nobody holds, or whose offer is
 *       still out, then the ones not sent to the kitchen yet (muted);
 *     - **With a driver** — every bag a driver holds past the offer, with its
 *       chip and its dispatch controls;
 *     - **Waiting to collect** — one card per driver (T20 settlement cards):
 *       the finished bags, the cash, and Settle;
 *  3. **Collected at the restaurant** — the pickups, each with its next step.
 *
 * A session that may not read the board gets its own delivery bags (and the
 * not-sent ones) in the pickups' row style instead, so none is lost.
 *
 * Presentational over the page's reads: `TableGridPage` owns the off-premise
 * grid, the board and the dispatch layer, and hands them in, so the tab count
 * and this body can never disagree.
 */
export function DeliveryTab({
  bills,
  rows,
  showWaiting,
  waitingLoading,
  waitingError,
  canManage,
  lookup,
  board,
  dispatchNow,
  skewMs,
  now,
  canOpen,
  dispatchChip,
  dispatchAction,
}: {
  bills: readonly DeliveryTabGridEntry[];
  rows: readonly WaitingRow[];
  /** `DISPATCH_MANAGE` or `DISPATCH_CASH_CONFIRM`: the waiting card is drawn. */
  showWaiting: boolean;
  /** The board has not answered yet: the card waits rather than guess. */
  waitingLoading: boolean;
  /** G24-M3: the board failed with nothing cached. */
  waitingError: unknown;
  canManage: boolean;
  lookup: (invoiceId: string) => BillDispatch | undefined;
  board: DispatchBoardResponse | undefined;
  dispatchNow: number;
  skewMs: number;
  /** The floor's minute clock (the kitchen chips' minutes). */
  now: number;
  canOpen: boolean;
  /** The bag's driver chip (`floorDispatch.chip`). */
  dispatchChip: (bill: DeliveryTabGridEntry) => ReactNode;
  /** The bag's dispatch controls (`floorDispatch.action(bill, "paper")`). */
  dispatchAction: (bill: DispatchTarget) => ReactNode;
}) {
  const t = useTranslate();
  const { data: session } = useSession();
  const canConfirmCash = hasPermission(session, PERMISSIONS.DISPATCH_CASH_CONFIRM);
  const canSettle = canConfirmCash && hasPermission(session, PERMISSIONS.INVOICE_SETTLE);
  const canPayOut = hasPermission(session, PERMISSIONS.SHIFT_MANAGE);
  // T20 — the same grant pair as the board; under `dispatch`, so the realtime
  // `floor` signal (settlement writes included, D-47) refreshes it.
  const settlements = useApiQuery<DriverSettlementsOpenResponse>(
    settlementsOpenKey,
    endpoints.restaurant.dispatchSettlementsOpen,
    { enabled: showWaiting, refetchOnWindowFocus: true },
  );
  const cards = showWaiting ? settlements.data?.cards : undefined;

  // A session that may not read the board has no waiting table; its own
  // delivery bags are listed in the same table style instead, so none is lost.
  const ownWithoutBoard = showWaiting
    ? []
    : bills
        .filter((bill) => isDispatchableBill(bill) && !isUnsentEmpty(bill, undefined))
        .map((bill) => ({ bill, kitchen: kitchenChip(bill, now) }));
  // Only from a board that answered: before that nobody is known to hold a bag.
  const held = showWaiting && board ? withDriverRows(bills, lookup, now) : [];
  const onCards = cards ? invoicesOnCards(cards) : new Set<string>();
  // G25 L5 (P28a): a platform bag a live card lists is that card's, not a pickup.
  const pickups = pickupRows(bills, now, onCards);
  const unsent = unsentOwnRows(bills, showWaiting ? lookup : () => undefined, now);

  return (
    <div data-ui="delivery-tab" data-testid="delivery-tab" className="flex flex-col">
      <OnlineOrdersStrip paper />

      {showWaiting ? (
        /* The three things that happen to a bag, side by side (2026-10-26):
           waiting for a driver → with the driver → cash to collect. */
        <div data-ui="dl-lanes" className="grid items-start">
          <WaitingForDriverCard
            rows={rows}
            unsent={unsent}
            loading={waitingLoading}
            error={waitingError}
            canManage={canManage}
            lookup={lookup}
            board={board}
            now={dispatchNow}
            skewMs={skewMs}
            canOpen={canOpen}
          />
          <WithDriverTable
            rows={held}
            canOpen={canOpen}
            chip={dispatchChip}
            action={dispatchAction}
            now={dispatchNow}
            problem={(row) => Boolean(row.entry?.current?.problemOpen)}
          />
          <DriverSettlementsCard
            cards={cards}
            loading={settlements.isLoading}
            error={settlements.error}
            onRetry={() => void settlements.refetch()}
            bills={bills}
            dispatchAction={dispatchAction}
            canOpen={canOpen}
            canSettle={canSettle}
            canPayOut={canPayOut}
            canViewProof={canConfirmCash}
          />
        </div>
      ) : (
        <>
          {ownWithoutBoard.length > 0 ? (
            <PickupOrdersTable
              rows={ownWithoutBoard}
              title={t((m) => m.dispatch.deliveryOrdersTitle)}
              testId="delivery-orders-card"
              canOpen={canOpen}
            />
          ) : null}
          {unsent.length > 0 ? (
            <PickupOrdersTable
              rows={unsent}
              title={t((m) => m.dispatch.unsentTitle)}
              testId="unsent-card"
              canOpen={canOpen}
            />
          ) : null}
        </>
      )}

      <PickupOrdersTable
        rows={pickups}
        title={t((m) => m.dispatch.pickupSectionTitle)}
        hint={t((m) => m.dispatch.pickupSectionHint)}
        canOpen={canOpen}
      />
    </div>
  );
}
